import { useTranslation } from 'react-i18next'

import type { LearnArticleBodyProps } from '@/features/aprender/articles'
import { DashList, LearnArticleLink, LearnSeriesNav, TipiLink } from '@/features/aprender/learnArticleShared'
import { cn } from '@/lib/utils'
import { LocaleLink } from '@/navigation/LocaleLink'
import { article as articleUi } from '@/shared/lib/articleStyles'
import { ArticleByline } from '@/shared/ui/ArticleByline'
import { ArticleCoverHero } from '@/shared/ui/ArticleCoverHero'
import { ArticleReadingLayout } from '@/shared/ui/ArticleReadingLayout'
import { ArticleToc } from '@/shared/ui/ArticleToc'
import '@/styles/food-cost-fonts.css'

const ARTICLE_ID = 'menu-navidad-grupos-escandallo'
const k = (field: string) => `learn.articles.${ARTICLE_ID}.${field}`

const THEFORK_HREF =
    'https://www.vinetur.com/2025112593665/las-cenas-de-navidad-en-grupo-ya-suponen-el-31-de-las-reservas-en-restaurantes-en-diciembre.html'
const DEIA_HREF = 'https://www.deia.eus/economia/2025/12/21/tardebuena-comidas-empresa-elevan-5-10498381.amp.html'
const OCU_HREF =
    'https://www.esdiario.com/comunidad-valenciana/251224/175527/cena-nochebuena-maa-cara-marisco-carnes-productos-tipicos-suben-56-mes.html'
const OCU_PRIMERA_HREF = 'https://www.ocu.org/organizacion/prensa/notas-de-prensa/2025/observatorionavidad1ola041225'

const SOURCES: { href?: string; labelKey: string; fallback: string }[] = [
    {
        href: THEFORK_HREF,
        labelKey: 'sources.1',
        fallback: 'TheFork, datos de reservas de grupo en diciembre de 2025, vía Vinetur (25 de noviembre de 2025).',
    },
    {
        href: DEIA_HREF,
        labelKey: 'sources.2',
        fallback:
            'Hostelería de España, análisis de la campaña de Navidad 2025, vía Deia / EFE (21 de diciembre de 2025).',
    },
    {
        href: OCU_HREF,
        labelKey: 'sources.3',
        fallback:
            'OCU, Observatorio de precios de Navidad 2025: tercer control, vía ESdiario (24 de diciembre de 2025).',
    },
    {
        href: OCU_PRIMERA_HREF,
        labelKey: 'sources.4',
        fallback: 'OCU, Observatorio de precios de Navidad 2025: primer control (4 de diciembre de 2025).',
    },
    {
        labelKey: 'sources.5',
        fallback:
            'Ejemplo de menú a 40 € y coste de barra libre por persona: elaboración propia de Tipi con precios orientativos de mayorista.',
    },
]

export function MenuNavidadGruposEscandalloArticle({ article }: LearnArticleBodyProps) {
    const { t } = useTranslation()

    const tocItems = [
        { id: 'precio-octubre', label: t(k('toc.1'), '1. El precio que mandaste en octubre ya no es tuyo') },
        { id: 'peso-grupos', label: t(k('toc.2'), '2. ¿Cuánto pesan los menús de Navidad para grupos?') },
        { id: 'paso-1', label: t(k('toc.3'), '3. Paso 1: el menú que más vendes, entero') },
        { id: 'paso-2', label: t(k('toc.4'), '4. Paso 2: escandállalo con precios de diciembre') },
        { id: 'paso-3', label: t(k('toc.5'), '5. Paso 3: la barra libre por persona') },
        { id: 'ejemplo', label: t(k('toc.6'), '6. Treinta personas, cuarenta euros') },
        { id: 'limites', label: t(k('toc.7'), '7. Lo que el escandallo no te arregla') },
        { id: 'matices', label: t(k('toc.8'), '8. Lo que ningún observatorio mide') },
    ]

    const costeRows = [
        {
            label: t(k('table.coste.1'), 'Lo que creías, a ojo'),
            value: t(k('table.coste.1c'), '10,90 €'),
        },
        {
            label: t(k('table.coste.2'), 'Escandallo completo, precios de octubre'),
            value: t(k('table.coste.2c'), '15,20 €'),
        },
        {
            label: t(k('table.coste.3'), 'Mismo escandallo, precios de diciembre'),
            value: t(k('table.coste.3c'), '16,30 €'),
            strong: true,
        },
    ]

    const barraItems = [
        t(k('list.barra.1'), 'Un combinado servido a 50 ml cuesta unos 1,75 €.'),
        t(
            k('list.barra.2'),
            'El mismo combinado servido a ojo, a 70 ml, que es como se sirve en barra libre, sube a unos 2,15 €.',
        ),
        t(k('list.barra.3'), 'A dos copas por hora durante dos horas, son 8,60 € por persona.'),
        t(
            k('list.barra.4'),
            'Con barra «hasta que cerremos», tres horas a tres copas por hora, son 19,35 € por persona.',
        ),
    ]

    return (
        <div className={articleUi.page}>
            <article className={articleUi.shell}>
                <ArticleCoverHero
                    category={article.category}
                    title={article.title}
                    dek={
                        <>
                            {t(
                                k('dek'),
                                'En diciembre de 2025, los grupos de seis o más personas fueron el 31 % de las reservas en TheFork. La mayoría de menús de Navidad para grupos se venden entre 30 y 55 euros por persona. El precio lo pones en octubre, pero la materia prima la pagas en diciembre: la cesta navideña que sigue la OCU subió un 10,3 % solo entre finales de noviembre y Nochebuena. Y un presupuesto ya aceptado no se puede subir. No hace falta escandallar todos tus menús: basta con el que más vendes, y esta semana.',
                            )}
                        </>
                    }
                    imageSrc={article.imagePath}
                    imageAlt={t(
                        k('heroAlt'),
                        'Cena de Navidad para grupos en un restaurante: mesa larga puesta con copas y platos',
                    )}
                    byline={
                        <ArticleByline
                            authorName={article.authorName}
                            readingMinutes={article.readingMinutes}
                            bylineBy={t(k('bylineBy'), 'Por')}
                        />
                    }
                />
                <ArticleReadingLayout
                    intro={
                        <div className={articleUi.proseIntro}>
                            <p className={cn(articleUi.p, articleUi.pGap2xl, 'text-article-muted-soft')}>
                                {t(k('published'), 'Publicado el 28 de octubre de 2026.')}
                            </p>
                        </div>
                    }
                    toc={
                        <ArticleToc
                            items={tocItems}
                            label={t(k('tocLabel'), 'En este artículo')}
                            ariaLabel={t(k('tocAria'), 'Índice de contenidos')}
                        />
                    }
                >
                    <blockquote className={articleUi.quote}>
                        {t(k('quote.hr'), '«¿Cuánto nos sale por cabeza con barra libre hasta las dos?»')}
                    </blockquote>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.open.1'),
                                'Te lo preguntó por teléfono la de recursos humanos de una empresa de treinta personas. Y diste una cifra en el momento, porque si tardas, llaman al de enfrente.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.open.2'),
                                'Esa cifra salió de la cabeza. Del menú del año pasado, un par de euros más caro, y de la sensación de que con los grupos siempre se gana.',
                            )}
                        </p>
                    </div>

                    <h2 id='precio-octubre' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.precio'), 'El precio que mandaste en octubre ya no es tuyo')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            <strong>
                                {t(k('p.precio.1Strong'), 'Un presupuesto de grupo aceptado es un precio cerrado.')}
                            </strong>{' '}
                            {t(
                                k('p.precio.1After'),
                                'Si la empresa ya te dijo que sí a 40 euros, en diciembre cobras 40 euros aunque la merluza haya subido.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.precio.2'),
                                'En lo ya firmado solo puedes tocar lo que va dentro del plato: gramaje, guarnición, proveedor y cuándo compras.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.precio.3'),
                                'En lo que todavía no está firmado puedes tocarlo todo, precio y condiciones de la bebida incluidos.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.precio.4'),
                                'Por eso el momento es ahora. En noviembre empiezan a sentarse los grupos y en diciembre ya no queda nada que negociar.',
                            )}
                        </p>
                    </div>

                    <h2 id='peso-grupos' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.peso'), '¿Cuánto pesan los menús de Navidad para grupos en diciembre?')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.peso.1'), 'Mucho. ')}
                            <strong>
                                {t(k('p.peso.1StrongBefore'), 'Los grupos de seis o más personas fueron el ')}
                                <a
                                    href={THEFORK_HREF}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className={articleUi.link}
                                >
                                    {t(k('p.peso.1Link'), '31 % de las reservas de diciembre de 2025 en TheFork')}
                                </a>
                                {t(k('p.peso.1StrongAfter'), ', un 47 % más que la media del resto del año.')}
                            </strong>
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            <strong>{t(k('p.peso.2Strong'), 'El grupo medio fue de diez personas')}</strong>
                            {t(
                                k('p.peso.2After'),
                                ', con un ticket medio de 28 euros en esa plataforma. El fin de semana del 12 al 14 de diciembre, los grupos fueron la mitad de todas las reservas.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.peso.3Before'), 'En precio, ')}
                            <strong>
                                {t(k('p.peso.3StrongBefore'), 'el ')}
                                <a
                                    href={DEIA_HREF}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className={articleUi.link}
                                >
                                    {t(
                                        k('p.peso.3Link'),
                                        '42,1 % de los clientes elige menús de 30 a 45 euros y el 31,6 %, de 45 a 55',
                                    )}
                                </a>
                            </strong>
                            {t(k('p.peso.3After'), ', según el análisis de campaña de Hostelería de España.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.peso.4Before'), 'Y la materia prima no espera. ')}
                            <strong>
                                {t(
                                    k('p.peso.4StrongBefore'),
                                    'Entre finales de noviembre y la víspera de Nochebuena de 2025, los 16 productos navideños que sigue la OCU ',
                                )}
                                <a href={OCU_HREF} target='_blank' rel='noopener noreferrer' className={articleUi.link}>
                                    {t(k('p.peso.4Link'), 'subieron un 10,3 % de media')}
                                </a>
                                {t(k('p.peso.4StrongAfter'), '.')}
                            </strong>
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.peso.5Before'), 'Los percebes, un ')}
                            <strong>{t(k('p.peso.5a'), '56 %')}</strong>
                            {t(k('p.peso.5Mid1'), '. Las almejas, un ')}
                            <strong>{t(k('p.peso.5b'), '43 %')}</strong>
                            {t(k('p.peso.5Mid2'), '. La pularda, un ')}
                            <strong>{t(k('p.peso.5c'), '26 %')}</strong>
                            {t(k('p.peso.5Mid3'), '. La merluza, un ')}
                            <strong>{t(k('p.peso.5d'), '20 %')}</strong>
                            {t(k('p.peso.5Mid4'), '. El besugo, un ')}
                            <strong>{t(k('p.peso.5e'), '17 %')}</strong>
                            {t(k('p.peso.5After'), '.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.peso.6'),
                                'Ese 10,3 % no incluye lo que ya había subido desde octubre, que es justo cuando tú pusiste el precio.',
                            )}
                        </p>
                    </div>

                    <h2 id='paso-1' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.paso1'), 'Paso 1: el menú que más vendes, entero')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.paso1.1'),
                                'No escandalles los cuatro menús. Coge el que más presupuestos aceptados o pedidos lleva.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.paso1.2'),
                                'La trampa es escandallar solo el principal. Un menú de grupo es aperitivo, entrantes al centro, principal, postre, pan, turrón, café, chupito y el cava del brindis.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.paso1.3'), 'Lo pequeño, multiplicado por treinta, deja de ser pequeño.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            <strong>{t(k('p.paso1.4Strong'), 'Esta semana:')}</strong>{' '}
                            {t(
                                k('p.paso1.4After'),
                                'apunta cada elemento de ese menú con el gramaje que de verdad sale de cocina, no el que pone la ficha. Pregunta cuánto ibérico lleva la tabla para diez.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(k('p.paso1.5Before'), 'Si nunca has hecho uno, aquí tienes ')}
                            <LearnArticleLink slug='como-hacer-un-escandallo-paso-a-paso'>
                                {t(k('p.paso1.5Link'), 'cómo se escandalla un plato, ingrediente a ingrediente')}
                            </LearnArticleLink>
                            {t(k('p.paso1.5After'), '.')}
                        </p>
                    </div>

                    <h2 id='paso-2' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.paso2'), 'Paso 2: escandállalo con precios de diciembre')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.paso2.1'),
                                'Aquí se rompe casi todo el mundo. Usas el albarán de octubre, te sale un coste bonito y te quedas tranquilo.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            <strong>
                                {t(
                                    k('p.paso2.2Strong'),
                                    'El coste que cuenta es el de la semana en que cocinas, no el del día en que presupuestas.',
                                )}
                            </strong>
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.paso2.3'),
                                'A cada ingrediente sensible ponle el precio que esperas pagar a mediados de diciembre. Al pescado, el marisco, el cordero y las aves, súbeles como mínimo lo que midió la OCU el año pasado.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            <strong>{t(k('p.paso2.4Strong'), 'Esta semana:')}</strong>{' '}
                            {t(
                                k('p.paso2.4After'),
                                'llama a tu proveedor de pescado y al de carne y pregunta si te cierran precio para tus fechas de grupo. Lo que aguanta bien el congelador, como el langostino o el marisco para salsas, cómpralo ya.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.paso2.5'),
                                'En los presupuestos que aún no estén firmados, plantéate cambiar el pescado más volátil por otro.',
                            )}
                        </p>
                    </div>

                    <h2 id='paso-3' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.paso3'), 'Paso 3: ¿cuánto te cuesta la barra libre por persona?')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.paso3.1'),
                                'La bebida es donde más se engaña uno, porque nunca se mide. Y en una cena de empresa se bebe, y no poco.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.paso3.2'),
                                'La barra libre no es un precio: es una cuenta abierta con fecha de cierre que pones tú.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.paso3.3'), 'Haz la cuenta por copa, no por botella. ')}
                            <em>
                                {t(
                                    k('p.paso3.3Em'),
                                    'Cuenta nuestra con precios de compra habituales de una ginebra de gama media y refresco de hostelería: tómala como orden de magnitud.',
                                )}
                            </em>
                        </p>
                    </div>
                    <DashList items={barraItems} />
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.paso3.4'),
                                'Si cobras 15 euros por la barra libre, te quedan 13,64 sin IVA. Con dos horas, cubres. Con tres horas sin límite, pierdes dinero con cada invitado.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            <strong>{t(k('p.paso3.5Strong'), 'Esta semana:')}</strong>{' '}
                            {t(
                                k('p.paso3.5After'),
                                'en todo presupuesto sin firmar, pon hora de cierre de barra, marcas concretas y medidor en la botella. El camarero de más va dentro del precio, no aparte.',
                            )}
                        </p>
                    </div>

                    <h2 id='ejemplo' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.ejemplo'), 'Treinta personas, cuarenta euros: la cena hecha números')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd, 'italic text-article-muted')}>
                            {t(
                                k('p.ejemplo.lead'),
                                'Ejemplo montado por nosotros con precios orientativos de mayorista para un menú tipo; no corresponde a ningún cliente.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.ejemplo.1Before'),
                                'Menú de 40 € con IVA, con vino, agua, cava y café. Sin IVA son ',
                            )}
                            <strong>{t(k('p.ejemplo.1Strong'), '36,36 €')}</strong>
                            {t(k('p.ejemplo.1After'), ' por persona.')}
                        </p>
                    </div>
                    <div className={articleUi.tableWrap}>
                        <table className={cn(articleUi.tableFacts, '[&_thead_th]:text-left')}>
                            <caption className='sr-only'>
                                {t(k('table.coste.caption'), 'Coste por persona del menú de 40 €')}
                            </caption>
                            <thead>
                                <tr>
                                    <th scope='col'>{t(k('table.coste.col1'), 'Coste por persona')}</th>
                                    <th scope='col'>{t(k('table.coste.col2'), 'Importe')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {costeRows.map(row => (
                                    <tr key={row.label}>
                                        <th scope='row'>{row.label}</th>
                                        <td className='tabular-nums'>
                                            {row.strong ? <strong>{row.value}</strong> : row.value}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.ejemplo.2'),
                                'La comida completa sale a 12,60 € y la bebida incluida a 2,60 €. En diciembre, entrantes y principal suben alrededor de un 12 %.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.ejemplo.3Before'), 'Creías ganar ')}
                            <strong>{t(k('p.ejemplo.3a'), '25,46 €')}</strong>
                            {t(k('p.ejemplo.3Mid'), ' por cubierto. Ganas ')}
                            <strong>{t(k('p.ejemplo.3b'), '20,06 €')}</strong>
                            {t(k('p.ejemplo.3After'), '. Son ')}
                            <strong>{t(k('p.ejemplo.3c'), '5,40 € menos por persona')}</strong>
                            {t(k('p.ejemplo.3End'), ', y un coste de materia prima del 45 %.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(k('p.ejemplo.4Before'), 'En una cena de treinta, ')}
                            <strong>{t(k('p.ejemplo.4a'), '162 €')}</strong>
                            {t(k('p.ejemplo.4Mid'), ' que no estaban en tu cuenta. En doce cenas así, ')}
                            <strong>{t(k('p.ejemplo.4b'), '1.944 €')}</strong>
                            {t(k('p.ejemplo.4After'), '. Y eso antes de que la barra libre sume o reste.')}
                        </p>
                    </div>

                    <h2 id='limites' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.limites'), 'Lo que el escandallo no te arregla')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.limites.1'),
                                'Saber el coste no te devuelve lo que ya firmaste: te dice cuánto vas a ganar de verdad.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.limites.2'),
                                'Tampoco mide el personal de refuerzo ni que un grupo de treinta te ocupe el comedor toda la noche, sin segundo turno. Eso va en el precio del próximo presupuesto, no en el plato.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.limites.3'),
                                'Y no cubre al grupo que no aparece. Sin señal, un grupo caído es materia prima comprada a precio de diciembre que no paga nadie.',
                            )}
                        </p>
                    </div>

                    <h2 id='matices' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.matices'), 'Lo que ningún observatorio mide')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.matices.1'),
                                'La OCU mide desde finales de noviembre y en mercados y supermercados, no en precio de mayorista. Lo que sube de octubre a diciembre en tu proveedor no lo publica nadie.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.matices.2'),
                                'El 31 % de TheFork son reservas en una plataforma, no facturación del sector. Qué parte de tu caja de diciembre son grupos solo lo sabes tú.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.matices.3'),
                                'Del consumo en barra libre no hay dato neutral en España. Tu mejor fuente son tus propias botellas vacías del año pasado.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.matices.4Before'),
                                'Si no sabes cuánto te cuesta de verdad el menú que más vendes y quieres arreglarlo esta semana, ',
                            )}
                            <TipiLink />
                            {t(
                                k('p.matices.4After'),
                                ' es el camino: se lo dictas por voz y lo calcula con los precios de tus propios albaranes.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(k('p.matices.5'), 'Uno. El que más vendes. Antes de que entre el primer grupo.')}
                        </p>
                    </div>
                </ArticleReadingLayout>

                <section className={articleUi.cta}>
                    <h2 className={cn(articleUi.h2, articleUi.h2Cta)}>
                        {t(k('h2.cta'), '¿Quieres escandallar el menú de Navidad que más vendes?')}
                    </h2>
                    <p className={articleUi.ctaSub}>
                        {t(
                            k('cta.sub'),
                            'Se lo dictas por voz y Tipi lo calcula con los precios de tus propios albaranes. Antes de que entre el primer grupo.',
                        )}
                    </p>
                    <div className={articleUi.ctaActionsCenter}>
                        <LocaleLink to='/#form' className={cn(articleUi.btn, articleUi.btnSolid)}>
                            {t(k('cta.button'), 'Empezar con Tipi')}
                        </LocaleLink>
                    </div>
                </section>

                <LearnSeriesNav currentId={article.id} />

                <section className={articleUi.sources} aria-labelledby='fuentes-heading'>
                    <h2 id='fuentes-heading' className={articleUi.sourcesTitle}>
                        {t(k('sourcesTitle'), 'Fuentes')}
                    </h2>
                    <ul className={articleUi.sourcesList}>
                        {SOURCES.map(source =>
                            source.href ? (
                                <li key={source.labelKey}>
                                    <a
                                        href={source.href}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        className={articleUi.link}
                                    >
                                        {t(k(source.labelKey), source.fallback)}
                                    </a>
                                </li>
                            ) : (
                                <li key={source.labelKey}>{t(k(source.labelKey), source.fallback)}</li>
                            ),
                        )}
                    </ul>
                </section>
            </article>
        </div>
    )
}
